/**
 * @fileoverview React Context for External Accounts within Listings Launch.
 *
 * @author Brian Piejak (bpiejak@yext.com) on 10/2/2023
 */
import ExternalAccount
  from '/js/yext/externalaccounts/entities/listingslaunch/model/ExternalAccount';
import {ExternalAccountsContext}
  from '/js/yext/externalaccounts/shared/listingslaunch/externalaccountscontext';

type Props = {
  defaultPublisherIdToExternalAccountId: Map<number, number>;
  children: React.ReactNode | undefined;
}

export function ExternalAccountsContextProvider(
  {defaultPublisherIdToExternalAccountId, children}: Props): React.ReactElement {
  const defaultExternalAccountMap = new Map<number, ExternalAccount>();
  defaultPublisherIdToExternalAccountId?.forEach((externalAccountId, publisherId) => {
    if (externalAccountId > 0) {
      defaultExternalAccountMap.set(publisherId, {id: externalAccountId, name: null, email: null, isValid: true});
    }
  });

  const [publisherIdToExternalAccount, setPublisherIdToExternalAccount] =
    React.useState<Map<number, ExternalAccount>>(defaultExternalAccountMap);

  const [
    publisherIdToUserLinkedExternalAccounts, setPublisherIdToUserLinkedExternalAccounts,
  ] = React.useState<Map<number, Array<ExternalAccount>>>(new Map());

  const taskflowContextValue = React.useMemo(() => ({
    publisherIdToExternalAccount,
    setPublisherIdToExternalAccount,
    publisherIdToUserLinkedExternalAccounts,
    setPublisherIdToUserLinkedExternalAccounts,
  }), [
    publisherIdToExternalAccount,
    publisherIdToUserLinkedExternalAccounts,
  ]);

  return (
    <ExternalAccountsContext.Provider value={taskflowContextValue}>
      {children}
    </ExternalAccountsContext.Provider>
  );
}
